import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createSession as apiCreate,
  deleteSession as apiDelete,
  listSessions,
  updateSession as apiUpdate,
} from '../services/chatApi';
import type { ChatSession } from '../services/chatApi';
import { showError } from '../utils/alert';

type Status = 'active' | 'archived';

const key = (status: Status) => ['chat-sessions', status] as const;

/**
 * The ONE fetcher for `['chat-sessions','active']`. Every observer on that key
 * must pass this exact function: React Query pushes each observer's options
 * onto the shared Query object, so an observer with a different `queryFn`
 * replaces it, and whichever refetch comes next runs through the substitute.
 */
const fetchActiveSessions = () => listSessions('active');

/**
 * Read-only observer of the cached active-session list — never fetches
 * (`enabled: false`): the list is owned by useChatSessions, this only reads
 * whatever is already in the cache. Leaf components (RowChips' ◔ draft chip)
 * use it to light up from sessions' `unsaved_artifacts` without owning the
 * session lifecycle.
 *
 * It still passes the owner's `queryFn` (see fetchActiveSessions) rather than a
 * `() => []` placeholder: a placeholder here blanked the whole session sidebar
 * whenever a WS catch-up refetch landed while this observer had been the last
 * writer on the key (2026-11-05).
 */
export function useActiveSessionsCached(): ChatSession[] {
  const { data } = useQuery({
    queryKey: key('active'),
    queryFn: fetchActiveSessions,
    enabled: false,
  });
  return data ?? [];
}

/**
 * Session list state + lifecycle mutations + current selection.
 *
 * Query keys are ['chat-sessions', 'active'|'archived']; the WS catch-up in
 * ChatConversation invalidates the ['chat-sessions'] prefix, covering both.
 * Archiving/deleting the CURRENT session auto-switches to the next active one
 * (or null -> the shell's empty state).
 *
 * `mainSessionId` (resolved by useMainSession) is preferred by the auto-select
 * over the most recent active session; the latest is only used when no main is
 * known (`null` = main resolve failed, `undefined` = not in play, e.g. a
 * route-provided deep-link session). A main resolving after the fallback pick
 * migrates the selection, but never over an explicit user choice.
 */
export function useChatSessions(initialSessionId?: string | null, mainSessionId?: string | null) {
  const qc = useQueryClient();
  const lastCreatedIdRef = useRef<string | null>(null);
  // The id the effect picked on its own (as opposed to a route param or an
  // explicit sidebar choice) — only auto-picks may be overwritten by a late
  // main-session resolve.
  const autoSelectedRef = useRef<string | null>(null);
  // The route-targeted id a vanish-confirm refetch was fired for (see the
  // reconcile effect): one authoritative re-check before self-healing it away.
  const routeVanishConfirmRef = useRef<string | null>(null);
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(initialSessionId ?? null);
  // First externally supplied id, used to detect a genuine prop CHANGE below.
  const lastInitialIdRef = useRef<string | null | undefined>(initialSessionId);

  const active = useQuery({ queryKey: key('active'), queryFn: fetchActiveSessions });
  const archived = useQuery({ queryKey: key('archived'), queryFn: () => listSessions('archived') });

  const currentSession = useMemo(
    () =>
      [...(active.data ?? []), ...(archived.data ?? [])].find((x) => x.id === currentSessionId) ??
      null,
    [active.data, archived.data, currentSessionId]
  );

  // Selection reconciliation: auto-select the main session (falling back to
  // the most recent active one) when nothing is open, and self-heal when the
  // open session disappears from both lists (deleted here or on another
  // device — lists refetch, selection must not keep pointing at a session
  // the API will 404 on).
  useEffect(() => {
    if (currentSessionId === null) {
      const target = mainSessionId ?? active.data?.[0]?.id;
      if (target) {
        autoSelectedRef.current = target;
        setCurrentSessionId(target);
      }
      return;
    }
    // Main resolved later than the fallback auto-pick: migrate onto it —
    // unless the user explicitly chose another session in the meantime (the
    // marker only tracks auto-picks, so an explicit pick is never overridden).
    if (
      mainSessionId &&
      currentSessionId !== mainSessionId &&
      currentSessionId === autoSelectedRef.current
    ) {
      autoSelectedRef.current = mainSessionId;
      setCurrentSessionId(mainSessionId);
      return;
    }
    // Skip a just-created session: it is selected optimistically before the
    // list refetch lands, so "not in cache yet" is not "vanished".
    if (
      active.isSuccess &&
      archived.isSuccess &&
      !currentSession &&
      currentSessionId !== lastCreatedIdRef.current &&
      // Same for a route-provided id (deep link / thesis-form handoff): while
      // the lists are still refetching on this mount, a pre-mount cached list
      // missing the id means "not fetched yet", not "deleted". Once the fetch
      // lands and the id is still absent, the self-heal below runs as usual
      // (bug A, 2026-10-04 — the self-heal used to yank the selection onto an
      // old session mid-handoff, dropping the deep-linked command).
      !(currentSessionId === initialSessionId && active.isFetching)
    ) {
      // A route-targeted session is AUTHORITATIVE: it was created moments
      // before the navigation (Feed 讨论/复盘, thesis-form handoff), so a
      // landed list that misses it can be a PRE-CREATE snapshot, not a
      // deletion — the Chat tab is the initial tab, its app-open fetch can
      // be the last writer and evict the handoff's seeded row even though a
      // post-create read would show the row. Self-healing then lands the
      // shell on the empty state (blank screen, command stranded — staging
      // report 2026-10-09). So the first vanish of a route id triggers ONE
      // confirming refetch (post-create, trustworthy) and only heals if that
      // fetch also lacks the row — a genuinely deleted route session still
      // recovers, one round-trip later.
      if (currentSessionId === initialSessionId && routeVanishConfirmRef.current !== currentSessionId) {
        routeVanishConfirmRef.current = currentSessionId;
        void active.refetch();
        return;
      }
      const target = mainSessionId ?? active.data?.[0]?.id ?? null;
      autoSelectedRef.current = target;
      setCurrentSessionId(target);
    }
  }, [
    active.data,
    active.isFetching,
    active.isSuccess,
    archived.data,
    archived.isSuccess,
    currentSession,
    currentSessionId,
    initialSessionId,
    mainSessionId,
  ]);

  // Follow a CHANGED external id (bug B, 2026-10-04 — re-entry handoff). The
  // thesis-form handoff navigates to an ALREADY-MOUNTED ChatScreen; react
  // navigation merges params instead of remounting, and useState's initializer
  // above only ran once — so the shell kept showing the previous session and
  // the deep-linked initialCommand was dropped (appliesToRoute never became
  // true). Adopt the id only when the prop GENUINELY changed: lastInitialIdRef
  // starts at the first prop, so re-renders carrying the same value (every
  // unrelated useRoute re-render) never clobber a sidebar switch, and
  // null/undefined never force a selection. Declared AFTER the reconcile
  // effect so adoption wins any same-commit race with auto-select/self-heal.
  useEffect(() => {
    if (initialSessionId != null && initialSessionId !== lastInitialIdRef.current) {
      lastInitialIdRef.current = initialSessionId;
      setCurrentSessionId(initialSessionId);
    }
  }, [initialSessionId]);

  const invalidate = (...statuses: Status[]) =>
    statuses.forEach((st) => qc.invalidateQueries({ queryKey: key(st) }));

  const switchFrom = (id: string) => {
    if (id !== currentSessionId) return;
    const next = (active.data ?? []).find((x) => x.id !== id) ?? null;
    setCurrentSessionId(next?.id ?? null);
  };

  const create = useMutation({
    mutationFn: () => apiCreate(),
    onSuccess: (created) => {
      lastCreatedIdRef.current = created.id;
      invalidate('active');
      setCurrentSessionId(created.id);
    },
  });
  const rename = useMutation({
    mutationFn: ({ id, title }: { id: string; title: string }) => apiUpdate(id, { title }),
    onSuccess: () => invalidate('active', 'archived'),
  });
  const archive = useMutation({
    mutationFn: (id: string) => apiUpdate(id, { status: 'archived' }),
    onSuccess: (_data, id) => {
      invalidate('active', 'archived');
      switchFrom(id);
    },
  });
  const unarchive = useMutation({
    mutationFn: (id: string) => apiUpdate(id, { status: 'active' }),
    onSuccess: () => invalidate('active', 'archived'), // current selection stays
  });
  const remove = useMutation({
    mutationFn: (id: string) => apiDelete(id),
    onSuccess: (_data, id) => {
      invalidate('active', 'archived');
      switchFrom(id);
    },
    onError: (err) => {
      showError(err instanceof Error ? err.message : 'Failed to delete session');
    },
  });

  return {
    activeSessions: active.data ?? [],
    archivedSessions: archived.data ?? [],
    isLoading: active.isLoading,
    currentSessionId,
    currentSession,
    setCurrentSessionId,
    createSession: create.mutateAsync,
    renameSession: async (id: string, title: string) => {
      await rename.mutateAsync({ id, title });
    },
    archiveSession: archive.mutateAsync,
    unarchiveSession: unarchive.mutateAsync,
    deleteSession: remove.mutateAsync,
  };
}
